import type { StaffItineraryEditBody } from "@lafayette/schemas";

import { isKnownZone, repeatedChoiceOf } from "./time";
import { type Item, type Section, wallOf } from "./view-model";

// The forms of the editor and the operation lists they build. Every action becomes the operation list of `editItineraryDraft`,
// `applyItineraryEdit` or `proposeItineraryEdit` (contract section 3.3): items are named by `stableId` (the lineage id), and a time
// is always a wall time with an explicit zone. `EDIT_ITEM` carries only the fields that changed, so every other field and every other
// item stays as it was. Nothing here builds a UTC instant.

export type Operation = StaffItineraryEditBody["operations"][number];
type AddItemOperation = Extract<Operation, { op: "ADD_ITEM" }>;
type EditChanges = Extract<Operation, { op: "EDIT_ITEM" }>["changes"];
type Timing = AddItemOperation["item"]["timing"];
type WallTime = Extract<Timing, { timingKind: "SCHEDULED" }>["start"];

export type Disambiguation = "reject" | "earlier" | "later";

export type WallTimeForm = {
  date: string;
  time: string;
  timeZone: string;
  disambiguation: Disambiguation;
};

export type TimingForm =
  | { kind: "UNTIMED" }
  | { kind: "ALL_DAY" }
  | {
      kind: "SCHEDULED";
      start: WallTimeForm;
      /** How the item ends: a length, an end wall time (a flight lands in another zone), or neither. */
      endMode: "DURATION" | "END";
      durationMinutes: string;
      end: WallTimeForm;
    };

const emptyWallTime = (timeZone: string, date = ""): WallTimeForm => ({
  date,
  time: "",
  timeZone,
  disambiguation: "reject",
});

export function wallTimeOf(form: WallTimeForm): WallTime {
  return {
    date: form.date,
    time: form.time,
    timeZone: form.timeZone.trim(),
    ...(form.disambiguation === "reject" ? {} : { disambiguation: form.disambiguation }),
  };
}

export function timingOf(form: TimingForm): Timing {
  if (form.kind !== "SCHEDULED") return { timingKind: form.kind };
  return form.endMode === "END"
    ? { timingKind: "SCHEDULED", start: wallTimeOf(form.start), end: wallTimeOf(form.end) }
    : {
        timingKind: "SCHEDULED",
        start: wallTimeOf(form.start),
        ...(form.durationMinutes.trim() === ""
          ? {}
          : { durationMinutes: Number(form.durationMinutes) }),
      };
}

export type FieldErrors = Readonly<Record<string, string>>;

const DATE = /^\d{4}-\d{2}-\d{2}$/u;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/u;

function wallTimeErrors(prefix: string, label: string, form: WallTimeForm): FieldErrors {
  const out: Record<string, string> = {};
  if (!DATE.test(form.date)) out[`${prefix}-date`] = `Enter the ${label} date.`;
  if (!TIME.test(form.time))
    out[`${prefix}-time`] = `Enter the ${label} time as hours and minutes.`;
  if (!isKnownZone(form.timeZone.trim())) {
    out[`${prefix}-zone`] = "Choose a time zone from the list, for example Europe/London.";
  }
  return out;
}

/** Checks the fields the page can check itself. The server checks the rest (gaps, overlaps, the day an item belongs to). */
export function timingErrors(form: TimingForm): FieldErrors {
  if (form.kind !== "SCHEDULED") return {};
  const errors: Record<string, string> = { ...wallTimeErrors("start", "start", form.start) };
  if (form.endMode === "END") {
    Object.assign(errors, wallTimeErrors("end", "end", form.end));
  } else if (form.durationMinutes.trim() !== "") {
    const minutes = Number(form.durationMinutes);
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 10_080) {
      errors.duration = "Enter the length in whole minutes, from 1 to 10080.";
    }
  }
  return errors;
}

// --- add item -----------------------------------------------------------------------------------------------------------------

export const ADDABLE_KINDS = ["CATALOG_ITEM", "CUSTOM_ACTIVITY", "TRANSIT", "BUFFER"] as const;
export type AddableKind = (typeof ADDABLE_KINDS)[number];

export type NewItemForm = {
  kind: AddableKind;
  catalogItemId: string;
  title: string;
  locationName: string;
  locality: string;
  countryCode: string;
  itemStatus: "PLANNED" | "TENTATIVE";
  timing: TimingForm;
  isFixed: boolean;
  weatherSensitivity: Item["weatherSensitivity"];
  customerNote: string;
  staffNote: string;
};

const textOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

export function newItemErrors(form: NewItemForm): FieldErrors {
  const errors: Record<string, string> = { ...timingErrors(form.timing) };
  if (form.kind === "CATALOG_ITEM") {
    if (form.catalogItemId === "")
      errors.catalog = "Search for a published catalog item and choose one.";
  } else if (form.title.trim() === "") {
    errors.title = "Enter what the customer will read as the title.";
  }
  if (form.countryCode.trim() !== "" && !/^[A-Z]{2}$/u.test(form.countryCode.trim())) {
    errors.country = "Use the two-letter country code in capitals, for example PT.";
  }
  return errors;
}

export function addItemOperation(section: Section, form: NewItemForm): Operation {
  const item: AddItemOperation["item"] = {
    kind: form.kind,
    itemStatus: form.itemStatus,
    timing: timingOf(form.timing),
    isFixed: form.isFixed,
    weatherSensitivity: form.weatherSensitivity,
    ...(form.kind === "CATALOG_ITEM" ? { catalogItemId: form.catalogItemId } : {}),
    ...(form.title.trim() === "" ? {} : { title: form.title.trim() }),
    ...(textOrNull(form.locationName) === null ? {} : { locationName: form.locationName.trim() }),
    ...(textOrNull(form.locality) === null ? {} : { locality: form.locality.trim() }),
    ...(textOrNull(form.countryCode) === null ? {} : { countryCode: form.countryCode.trim() }),
    ...(textOrNull(form.customerNote) === null ? {} : { customerNote: form.customerNote.trim() }),
    ...(textOrNull(form.staffNote) === null ? {} : { staffNote: form.staffNote.trim() }),
  };
  return { op: "ADD_ITEM", section: { sectionId: section.id }, item };
}

export function emptyNewItem(section: Section | null): NewItemForm {
  const zone = section?.timeZone ?? "";
  return {
    kind: "CATALOG_ITEM",
    catalogItemId: "",
    title: "",
    locationName: "",
    locality: "",
    countryCode: "",
    itemStatus: "PLANNED",
    timing: {
      kind: "SCHEDULED",
      start: emptyWallTime(zone, section?.localDate ?? ""),
      endMode: "DURATION",
      durationMinutes: "60",
      end: emptyWallTime(zone, section?.localDate ?? ""),
    },
    isFixed: false,
    weatherSensitivity: "NONE",
    customerNote: "",
    staffNote: "",
  };
}

// --- edit item ----------------------------------------------------------------------------------------------------------------

export type EditForm = {
  title: string;
  itemStatus: Item["itemStatus"];
  customerNote: string;
  staffNote: string;
  locationName: string;
  locality: string;
  countryCode: string;
  isFixed: boolean;
  weatherSensitivity: Item["weatherSensitivity"];
  timing: TimingForm;
  /** The staff member changed a time field. Without it the edit sends no `timing`, so an unchanged time is never re-judged. */
  timingChanged: boolean;
};

/** The timing of an item as the form edits it: wall times read in the item's own zones. */
export function timingFormOf(item: Item): TimingForm {
  const wall = wallOf(item);
  const localStart = wall.localStart;
  if (
    item.timingKind !== "SCHEDULED" ||
    item.startsAt === null ||
    item.timeZone === null ||
    localStart === null
  ) {
    return { kind: item.timingKind === "ALL_DAY" ? "ALL_DAY" : "UNTIMED" };
  }
  // The wall times are the server's (`view.localStart`, `view.localEnd`): the form never converts a stored instant itself.
  const startMs = Date.parse(item.startsAt);
  const start = { date: localStart.slice(0, 10), time: localStart.slice(11, 16) };
  const hasEnd = item.endsAt !== null && wall.localEnd !== null;
  const endZone = item.endTimeZone ?? item.timeZone;
  const end =
    hasEnd && wall.localEnd !== null
      ? { date: wall.localEnd.slice(0, 10), time: wall.localEnd.slice(11, 16) }
      : start;
  return {
    kind: "SCHEDULED",
    start: {
      ...start,
      timeZone: item.timeZone,
      disambiguation: repeatedChoiceOf(startMs, item.timeZone) ?? "reject",
    },
    endMode: hasEnd ? "END" : "DURATION",
    durationMinutes: item.durationMinutes === null ? "" : String(item.durationMinutes),
    end: {
      ...end,
      timeZone: endZone,
      disambiguation: hasEnd
        ? (repeatedChoiceOf(Date.parse(item.endsAt as string), endZone) ?? "reject")
        : "reject",
    },
  };
}

export function editFormOf(item: Item): EditForm {
  return {
    title: item.title,
    itemStatus: item.itemStatus,
    customerNote: item.customerNote ?? "",
    staffNote: item.staffNote ?? "",
    locationName: item.locationName ?? "",
    locality: item.locality ?? "",
    countryCode: item.countryCode ?? "",
    isFixed: item.isFixed,
    weatherSensitivity: item.weatherSensitivity,
    timing: timingFormOf(item),
    timingChanged: false,
  };
}

/** Only the fields that differ from the item. `null` when nothing changed. */
export function changesOf(item: Item, form: EditForm): EditChanges | null {
  const changes: Record<string, unknown> = {};
  const nullableText = (key: string, was: string | null, now: string) => {
    const next = textOrNull(now);
    if (next !== was) changes[key] = next;
  };
  if (form.title.trim() !== item.title) changes.title = form.title.trim();
  if (form.itemStatus !== item.itemStatus) changes.itemStatus = form.itemStatus;
  nullableText("customerNote", item.customerNote, form.customerNote);
  nullableText("staffNote", item.staffNote, form.staffNote);
  nullableText("locationName", item.locationName, form.locationName);
  nullableText("locality", item.locality, form.locality);
  nullableText("countryCode", item.countryCode, form.countryCode);
  if (form.isFixed !== item.isFixed) changes.isFixed = form.isFixed;
  if (form.weatherSensitivity !== item.weatherSensitivity) {
    changes.weatherSensitivity = form.weatherSensitivity;
  }
  if (form.timingChanged) changes.timing = timingOf(form.timing);
  return Object.keys(changes).length === 0 ? null : (changes as EditChanges);
}

export function editItemErrors(form: EditForm): FieldErrors {
  const errors: Record<string, string> = form.timingChanged ? { ...timingErrors(form.timing) } : {};
  if (form.title.trim() === "") errors.title = "The title cannot be empty.";
  if (form.countryCode.trim() !== "" && !/^[A-Z]{2}$/u.test(form.countryCode.trim())) {
    errors.country = "Use the two-letter country code in capitals, for example PT.";
  }
  return errors;
}

export const editItemOperation = (item: Item, changes: EditChanges): Operation => ({
  op: "EDIT_ITEM",
  stableId: item.lineageId,
  changes,
});

// --- move, remove, add day ----------------------------------------------------------------------------------------------------

export type MoveForm = {
  sectionId: string;
  start: WallTimeForm;
  startChanged: boolean;
};

export function moveFormOf(item: Item, section: Section): MoveForm {
  const timing = timingFormOf(item);
  return {
    sectionId: section.id,
    start:
      timing.kind === "SCHEDULED"
        ? timing.start
        : emptyWallTime(section.timeZone ?? "", section.localDate ?? ""),
    startChanged: false,
  };
}

export function moveErrors(item: Item, form: MoveForm, from: Section): FieldErrors {
  const errors: Record<string, string> = {};
  const movesDay = form.sectionId !== from.id;
  const needsStart = item.timingKind === "SCHEDULED" && (movesDay || form.startChanged);
  if (needsStart) Object.assign(errors, wallTimeErrors("start", "new start", form.start));
  if (item.timingKind === "SCHEDULED" && movesDay && !form.startChanged) {
    errors["start-time"] = "A timed item that moves to another day needs its new start time.";
  }
  if (!movesDay && !form.startChanged)
    errors["start-time"] = "Choose another day or change the time.";
  return errors;
}

export function moveItemOperation(item: Item, from: Section, form: MoveForm): Operation {
  const movesDay = form.sectionId !== from.id;
  const sendsStart = item.timingKind === "SCHEDULED" && (movesDay || form.startChanged);
  return {
    op: "MOVE_ITEM",
    stableId: item.lineageId,
    ...(movesDay ? { toSection: { sectionId: form.sectionId } } : {}),
    ...(sendsStart ? { start: wallTimeOf(form.start) } : {}),
  };
}

export const removeItemOperation = (item: Item): Operation => ({
  op: "REMOVE_ITEM",
  stableId: item.lineageId,
});

export type DayForm = { date: string; timeZone: string; title: string };

export function dayErrors(form: DayForm): FieldErrors {
  const errors: Record<string, string> = {};
  if (!DATE.test(form.date)) errors["day-date"] = "Enter the date of the day.";
  if (!isKnownZone(form.timeZone.trim())) {
    errors["day-zone"] = "Choose a time zone from the list, for example Europe/London.";
  }
  return errors;
}

export const addDayOperation = (form: DayForm): Operation => ({
  op: "ADD_SECTION",
  section: {
    kind: "DAY",
    localDate: form.date,
    timeZone: form.timeZone.trim(),
    ...(textOrNull(form.title) === null ? {} : { title: form.title.trim() }),
  },
});
